import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  ClockIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { isEqual, isNumber } from 'lodash'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../constants/track-events'
import {
  useAivyContext,
  useLanguageContext,
  usePartnerContext,
  useProductTourContext
} from '../../context'
import { mutation } from '../../graphql'
import { trackEvent } from '../../helpers/analytics'
import {
  DREAMTEAM_DIMENSIONS,
  getLongItems,
  getMovedFromRanks,
  hasLongKeys,
  longItemsToState
} from '../../helpers/culture-ranking'
import { CULTURAL_FIT_MODES } from '../../helpers/diagnostic'
import { useCreateCareerNavigation } from '../../hooks/use-create-career-navigation'
import { useMount } from '../../hooks/use-mount'
import { useSetState } from '../../hooks/use-set-state'
import CulturalFitQuestions from '../cultural-fit/cultural-fit-questions'
import {
  LongProfilePreview,
  ShortProfilePreview
} from '../cultural-fit/culture-profile-preview'
import DreamteamRanking from '../cultural-fit/dreamteam-ranking'
import Button from '../tailwind/Button'
import CreateCareerFooter from './CreateCareerFooter'

const hasShortKeys = (cultural_fit) =>
  isNumber(cultural_fit.HEROS_JOURNEY_score_coopcomp) &&
  isNumber(cultural_fit.HEROS_JOURNEY_score_orderchaos)

const getShortState = (cultural_fit) => ({
  HEROS_JOURNEY_score_coopcomp: cultural_fit.HEROS_JOURNEY_score_coopcomp,
  HEROS_JOURNEY_score_orderchaos: cultural_fit.HEROS_JOURNEY_score_orderchaos
})

const MODE_SHORT = CULTURAL_FIT_MODES.SHORT
const MODE_LONG = CULTURAL_FIT_MODES.LONG

const EDITOR_ID = 'cultural-fit-editor'

const CulturalFit = ({ career, careerMutation }) => {
  const { t } = useTranslation()
  const { system } = useAivyContext()
  const { language } = useLanguageContext()
  const { partner, refetchPartner } = usePartnerContext()

  const cultureDimensions = system?.dimensions_culture?.[language] ?? null

  const meta = JSON.parse(career.customer_dashboard_meta || '{}')
  const cultural_fit_career = JSON.parse(
    career.app_settings?.cultural_fit || '{}'
  )
  const cultural_fit_partner = JSON.parse(
    partner.app_settings?.cultural_fit || '{}'
  )

  const partner_has_short_profile = hasShortKeys(cultural_fit_partner)
  const partner_has_long_profile = hasLongKeys(cultural_fit_partner)

  // CULTURE existiert im Frontend nur als exam_id-String in diagnostic.js -
  // Titel, Bild, Dauer und dimensionSynonym kommen komplett aus dem
  // Backend-System-Item CHALLENGES. Fehlt der Eintrag dort, crasht Schritt 4
  // (confirm-testbattery getDuration) und dauerhaft die Stellen-Detailseite.
  // Kein stiller Fallback auf HEROS_JOURNEY: eine Stelle auszuliefern, deren
  // Testbatterie nicht zum Anforderungsprofil passt, ist teurer als ein
  // sichtbar gesperrter Modus. Eine nicht geseedete Stage degradiert damit auf
  // Kompakt statt kaputte Stellen zu erzeugen.
  //
  // Auf active === true statt auf blosse Existenz, weil die Talentpool-Auswahl
  // (shared/flex-configuration/challenges.js) genauso filtert. Bei einem
  // inaktiven CULTURE-Eintrag waere der Modus hier sonst waehlbar, dort aber
  // gaebe es keine Culture-Challenge mehr zum Ankreuzen.
  const isLongModeAvailable = system.challenges?.CULTURE?.active === true

  const initMode = () => {
    const preselected =
      cultural_fit_career.mode || cultural_fit_partner.mode || MODE_SHORT

    // Geklemmt: ein Partner-Default 'long' darf den Step nicht auf einem
    // deaktivierten Modus starten lassen.
    if (preselected === MODE_LONG && !isLongModeAvailable) return MODE_SHORT

    return preselected
  }

  const [mode, setMode] = useState(initMode)

  const initShortState = () => {
    if (hasShortKeys(cultural_fit_career))
      return getShortState(cultural_fit_career)
    if (partner_has_short_profile) return getShortState(cultural_fit_partner)
    return {}
  }

  const initLongItems = () => {
    if (hasLongKeys(cultural_fit_career))
      return getLongItems(cultural_fit_career)
    if (partner_has_long_profile) return getLongItems(cultural_fit_partner)
    return [...DREAMTEAM_DIMENSIONS]
  }

  const [shortState, setShortState] = useSetState(initShortState)
  const [dreamteamItems, setDreamteamItems] = useState(initLongItems)
  const [isLoadingNext, setIsLoadingNext] = useState(false)

  const {
    setProductTourState,
    productTourState: { tourActive }
  } = useProductTourContext()

  useMount(() => {
    trackEvent(trackEvents.CREATE_CAREER_STEP_2_VIEWED)

    if (tourActive) {
      setTimeout(() => {
        setProductTourState({ run: true })
      }, 0.4 * 1000)
    }
  })

  const { isLoadingBack, handleBackNavigation, handleNextNavigation } =
    useCreateCareerNavigation({ career, careerMutation })

  const resetToPartnerShort = () =>
    setShortState(getShortState(cultural_fit_partner))
  const resetToPartnerLong = () =>
    setDreamteamItems(getLongItems(cultural_fit_partner))

  const shortDiffersFromPartner =
    partner_has_short_profile &&
    !isEqual(shortState, getShortState(cultural_fit_partner))

  const longDiffersFromPartner =
    partner_has_long_profile &&
    !isEqual(
      longItemsToState(dreamteamItems),
      longItemsToState(getLongItems(cultural_fit_partner))
    )

  // Referenz fuer "was habe ich verschoben". Ohne Unternehmensprofil gibt es
  // nichts zu vergleichen, dann liefert getMovedFromRanks ein leeres Objekt und
  // es erscheint kein Marker.
  const partnerLongRanks = partner_has_long_profile
    ? longItemsToState(getLongItems(cultural_fit_partner))
    : null

  // movedCount > 0 ist im Long-Modus aequivalent zu longDiffersFromPartner, das
  // sichert getMovedFromRanks zu und ist dort erschoepfend getestet. Das Panel
  // kann also nie "angepasst" und gleichzeitig "0 verschoben" zeigen.
  const movedFromRanks = getMovedFromRanks(dreamteamItems, partnerLongRanks)
  const movedCount = Object.keys(movedFromRanks).length

  // Editor-Startzustand pro Modus getrennt: Unternehmensprofile sind pro Modus
  // (die Settings schreiben nur SHORT), also koexistieren "kompakt =
  // uebernommen, eingeklappt" und "detailliert = muss ausgefuellt werden,
  // offen". Verglichen wird das GESPEICHERTE Stellenprofil gegen das
  // Unternehmensprofil, nicht der Live-State - sonst klappt der Editor mitten
  // im Editieren wieder zu.
  const careerDiffersInShort =
    partner_has_short_profile &&
    hasShortKeys(cultural_fit_career) &&
    !isEqual(
      getShortState(cultural_fit_career),
      getShortState(cultural_fit_partner)
    )

  const careerDiffersInLong =
    partner_has_long_profile &&
    hasLongKeys(cultural_fit_career) &&
    !isEqual(
      longItemsToState(getLongItems(cultural_fit_career)),
      longItemsToState(getLongItems(cultural_fit_partner))
    )

  const [expandedModes, setExpandedModes] = useState(() => ({
    [MODE_SHORT]: !partner_has_short_profile || careerDiffersInShort,
    [MODE_LONG]: !partner_has_long_profile || careerDiffersInLong
  }))

  const handleModeChange = (newMode) => {
    setMode(newMode)
    trackEvent(trackEvents.CREATE_CAREER_CULTURAL_FIT_MODE_SELECTED, {
      mode: newMode
    })
  }

  const hasPartnerProfileForMode =
    mode === MODE_SHORT ? partner_has_short_profile : partner_has_long_profile
  const differsFromPartner =
    mode === MODE_SHORT ? shortDiffersFromPartner : longDiffersFromPartner
  const resetToPartner =
    mode === MODE_SHORT ? resetToPartnerShort : resetToPartnerLong

  // Ohne Unternehmensprofil gibt es nichts zu uebernehmen - dann muss der
  // Editor offen sein. Sonst waeren im Kompakt-Modus die beiden Fragen
  // unsichtbar, die "Weiter" ueberhaupt erst freischalten.
  const isEditorExpanded = !hasPartnerProfileForMode || expandedModes[mode]

  // Dreistufig, damit Headline und Button nicht auseinanderlaufen: eingeklappt
  // und schon angepasst darf nicht "Für diese Stelle anpassen" heissen.
  const toggleActionKey = isEditorExpanded
    ? 'create_career.cultural_fit.adopted.collapse_action'
    : differsFromPartner
      ? 'create_career.cultural_fit.adopted.edit_action'
      : 'create_career.cultural_fit.adopted.customize_action'

  const toggleEditor = () => {
    const willExpand = !expandedModes[mode]
    setExpandedModes((prev) => ({ ...prev, [mode]: willExpand }))

    if (!willExpand) return

    trackEvent(trackEvents.CREATE_CAREER_CULTURAL_FIT_CUSTOMIZE_STARTED, {
      mode
    })

    // Der Editor liegt unter der Karte - ohne Scroll sieht der Klick
    // wirkungslos aus. setTimeout, damit das Element schon gerendert ist.
    setTimeout(() => {
      document
        .getElementById(EDITOR_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)
  }

  const handleResetToPartner = () => {
    resetToPartner()
    trackEvent(trackEvents.CREATE_CAREER_CULTURAL_FIT_RESET_TO_COMPANY, {
      mode
    })
  }

  const next = async () => {
    setIsLoadingNext(true)

    const isShortMode = mode === MODE_SHORT
    const partner_profile_used = isShortMode
      ? partner_has_short_profile && !shortDiffersFromPartner
      : partner_has_long_profile && !longDiffersFromPartner

    trackEvent(
      isShortMode
        ? partner_profile_used
          ? trackEvents.CREATE_CAREER_CULTURAL_FIT_PARTNER_PROFILE_USED
          : trackEvents.CREATE_CAREER_CULTURAL_FIT_CUSTOM_PROFILE_USED
        : partner_profile_used
          ? trackEvents.CREATE_CAREER_CULTURAL_FIT_DREAMTEAM_PARTNER_PROFILE_USED
          : trackEvents.CREATE_CAREER_CULTURAL_FIT_DREAMTEAM_CUSTOM_PROFILE_USED,
      {
        mode,
        editor_opened: !!expandedModes[mode],
        partner_profile_available: hasPartnerProfileForMode
      }
    )

    const newCulturalFit = {
      mode,
      ...(isShortMode ? shortState : longItemsToState(dreamteamItems))
    }

    // Eine unberuehrte Default-Sortierung ist byte-identisch zu einer bewussten
    // Priorisierung (hasLongKeys ist immer true, weil longItemsToState immer
    // zehn Zahlen liefert). Ohne diesen Guard wuerde ein einziges "Weiter" die
    // Deklarationsreihenfolge zum Unternehmensstandard machen.
    const longTouched = !isEqual(dreamteamItems, [...DREAMTEAM_DIMENSIONS])

    const shouldUpdatePartner = isShortMode
      ? !partner_has_short_profile
      : !partner_has_long_profile && longTouched

    if (shouldUpdatePartner) {
      await mutation({
        mutation: 'updatePartner',
        input: {
          id: partner.id,
          app_settings: {
            ...(partner.app_settings || {}),
            cultural_fit: JSON.stringify({
              ...cultural_fit_partner,
              mode,
              ...newCulturalFit
            })
          }
        }
      })
    }

    const onSuccess = () => {
      refetchPartner()
      setIsLoadingNext(false)
    }

    handleNextNavigation({
      input: {
        app_settings: {
          ...(career.app_settings || {}),
          cultural_fit: JSON.stringify(newCulturalFit)
        },
        customer_dashboard_meta: JSON.stringify({
          ...meta,
          partner_profile_used
        })
      },
      onSuccess
    })
  }

  const modeOptions = [
    {
      value: MODE_SHORT,
      label: t('create_career.cultural_fit.mode_short'),
      description: t('create_career.cultural_fit.mode_short_description'),
      duration: t('create_career.cultural_fit.mode_short_duration'),
      scope: t('create_career.cultural_fit.mode_short_scope'),
      hasCompanyProfile: partner_has_short_profile
    },
    {
      value: MODE_LONG,
      label: t('create_career.cultural_fit.mode_long'),
      description: t('create_career.cultural_fit.mode_long_description'),
      duration: t('create_career.cultural_fit.mode_long_duration'),
      scope: t('create_career.cultural_fit.mode_long_scope'),
      hasCompanyProfile: partner_has_long_profile,
      disabled: !isLongModeAvailable
    }
  ]

  const benefits = [
    t('create_career.cultural_fit.benefit_1'),
    t('create_career.cultural_fit.benefit_2'),
    t('create_career.cultural_fit.benefit_3')
  ]

  return (
    <div className='mx-auto max-w-5xl'>
      <h1 className='text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl'>
        {t('create_career.cultural_fit.title')}
      </h1>
      <div className='w-full sm:w-3/4'>
        <p className='mt-3 text-base text-gray-600'>
          {t('create_career.cultural_fit.description')}
        </p>
        <ul className='mt-4 space-y-2'>
          {benefits.map((benefit) => (
            <li
              key={benefit}
              className='flex flex-row items-center gap-2 text-sm text-gray-700'
            >
              <CheckCircleIcon className='size-5 flex-shrink-0 text-primaryBlue' />
              {benefit}
            </li>
          ))}
        </ul>
      </div>

      {/* Mode selection */}
      <div className='mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2'>
        {modeOptions.map((option) => {
          const isSelected = mode === option.value
          const isDisabled = !!option.disabled
          return (
            <button
              key={option.value}
              type='button'
              disabled={isDisabled}
              onClick={() => handleModeChange(option.value)}
              className={clsx(
                'relative flex flex-col rounded-xl border-2 p-5 text-left transition-all',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                isDisabled && 'cursor-not-allowed border-gray-200 bg-gray-50',
                !isDisabled && isSelected && 'border-blue-600 bg-blue-50',
                !isDisabled &&
                  !isSelected &&
                  'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              )}
            >
              {isSelected && !isDisabled && (
                <CheckCircleIcon className='absolute right-4 top-4 h-5 w-5 text-blue-600' />
              )}
              <span
                className={clsx(
                  'pr-6 text-base font-semibold',
                  isDisabled
                    ? 'text-gray-400'
                    : isSelected
                      ? 'text-blue-900'
                      : 'text-gray-900'
                )}
              >
                {option.label}
              </span>
              <span
                className={clsx(
                  'mt-1 text-sm',
                  isDisabled ? 'text-gray-400' : 'text-gray-500'
                )}
              >
                {option.description}
              </span>
              <div className='mt-3 flex flex-wrap gap-2'>
                {[
                  { label: option.duration, Icon: ClockIcon },
                  { label: option.scope, Icon: null }
                ].map(({ label, Icon }) => (
                  <span
                    key={label}
                    className={clsx(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                      isSelected
                        ? 'bg-white text-blue-700 ring-blue-600/20'
                        : 'bg-gray-50 text-gray-600 ring-gray-500/10'
                    )}
                  >
                    {Icon && (
                      <Icon aria-hidden='true' className='h-3.5 w-3.5' />
                    )}
                    {label}
                  </span>
                ))}
                {/* Beantwortet "gibt es ueberhaupt eins?" am Entscheidungspunkt.
                    Kein CheckCircleIcon - das ist oben rechts der
                    Auswahl-Indikator. */}
                <span
                  className={clsx(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                    !isDisabled && option.hasCompanyProfile
                      ? 'bg-green-50 text-green-700 ring-green-600/20'
                      : 'bg-gray-50 text-gray-600 ring-gray-500/10'
                  )}
                >
                  <BuildingOffice2Icon
                    aria-hidden='true'
                    className='h-3.5 w-3.5'
                  />
                  {isDisabled
                    ? t('create_career.cultural_fit.mode_unavailable')
                    : option.hasCompanyProfile
                      ? t('create_career.cultural_fit.mode_profile_available')
                      : t('create_career.cultural_fit.mode_profile_missing')}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Wem gehoeren die Minuten? Feedback: "dauert es nun fuer mich 8 Min".
          Dass die Minuten den Talenten gehoeren, sagen die Chips auf den Cards
          schon selbst ("2 Min fuer Talente" gegen "2 Fragen fuer dich"), deshalb
          steht hier nur noch, was dort nicht steht. */}
      <p className='mt-3 w-full text-sm text-gray-500 md:w-9/12'>
        {t('create_career.cultural_fit.duration_hint_total', {
          step: t('create_career.step_41_title')
        })}
      </p>

      {/* Herkunft: uebernommen / fuer diese Stelle angepasst / noch keins */}
      {hasPartnerProfileForMode ? (
        <div
          className={clsx(
            'mt-8 rounded-xl border-2 p-5',
            differsFromPartner
              ? 'border-gray-200 bg-gray-50'
              : 'border-blue-100 bg-blue-50'
          )}
        >
          <div className='flex items-start gap-3'>
            {differsFromPartner ? (
              <PencilSquareIcon
                aria-hidden='true'
                className='mt-0.5 h-5 w-5 shrink-0 text-gray-500'
              />
            ) : (
              <CheckCircleIcon
                aria-hidden='true'
                className='mt-0.5 h-5 w-5 shrink-0 text-blue-600'
              />
            )}
            <div className='min-w-0 flex-1'>
              <div className='flex flex-wrap items-center gap-2'>
                <h2
                  className={clsx(
                    'text-base font-semibold',
                    differsFromPartner ? 'text-gray-900' : 'text-blue-900'
                  )}
                >
                  {t(
                    differsFromPartner
                      ? 'create_career.cultural_fit.adopted.customized_title'
                      : 'create_career.cultural_fit.adopted.title'
                  )}
                </h2>
                {/* Beantwortet "wie viel weicht ab" auch dann, wenn der Editor
                    eingeklappt ist und die Vorschau nur die Top 3 zeigt. */}
                {mode === MODE_LONG && movedCount > 0 && (
                  <span className='inline-flex shrink-0 items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20'>
                    {t('create_career.cultural_fit.adopted.moved_count', {
                      count: movedCount
                    })}
                  </span>
                )}
              </div>
              <p className='mt-1 text-sm text-gray-600'>
                {t(
                  differsFromPartner
                    ? 'create_career.cultural_fit.adopted.customized_description'
                    : 'create_career.cultural_fit.adopted.description'
                )}
              </p>
            </div>
          </div>

          <div className='mt-4 rounded-lg border border-gray-200 bg-white p-4'>
            {mode === MODE_SHORT ? (
              <ShortProfilePreview values={shortState} />
            ) : (
              <LongProfilePreview
                items={dreamteamItems}
                dimensions={cultureDimensions}
                movedFromRanks={movedFromRanks}
              />
            )}
          </div>

          <div className='mt-4 flex flex-wrap items-center gap-x-4 gap-y-2'>
            <Button.SecondaryLG
              onClick={toggleEditor}
              ariaExpanded={isEditorExpanded}
              ariaControls={EDITOR_ID}
              icon={<PencilSquareIcon aria-hidden='true' className='h-4 w-4' />}
              text={t(toggleActionKey)}
            />
            {(differsFromPartner || isEditorExpanded) && (
              // aria-disabled statt Ausblenden: ein verschwindendes
              // fokussiertes Element wirft Tastaturnutzer an <body>.
              // Vgl. dreamteam-ranking.js.
              <button
                type='button'
                onClick={differsFromPartner ? handleResetToPartner : undefined}
                aria-disabled={!differsFromPartner}
                className='flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 aria-disabled:cursor-default aria-disabled:text-gray-400 aria-disabled:hover:text-gray-400'
              >
                <ArrowPathIcon aria-hidden='true' className='h-4 w-4' />
                {t('create_career.cultural_fit.reset_to_company')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className='mt-8 flex items-start gap-3 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-5'>
          <BuildingOffice2Icon
            aria-hidden='true'
            className='mt-0.5 h-5 w-5 shrink-0 text-gray-400'
          />
          <div>
            <h2 className='text-base font-semibold text-gray-900'>
              {t('create_career.cultural_fit.first_time.title')}
            </h2>
            <p className='mt-1 text-sm text-gray-600'>
              {t(
                mode === MODE_SHORT
                  ? 'create_career.cultural_fit.first_time.description_short'
                  : 'create_career.cultural_fit.first_time.description_long'
              )}
            </p>
          </div>
        </div>
      )}

      {/* Wrapper bleibt gemountet, damit aria-controls immer aufloest */}
      <div id={EDITOR_ID} hidden={!isEditorExpanded}>
        {isEditorExpanded && (
          <>
            <p className='mt-8 w-full text-sm font-medium text-gray-700 md:w-9/12'>
              {mode === MODE_SHORT
                ? t('create_career.cultural_fit.short_intro')
                : t('create_career.cultural_fit.long_intro')}
            </p>
            <div className='mt-4'>
              {mode === MODE_SHORT ? (
                <CulturalFitQuestions
                  state={shortState}
                  setState={setShortState}
                />
              ) : (
                <DreamteamRanking
                  items={dreamteamItems}
                  setItems={setDreamteamItems}
                  dimensions={cultureDimensions}
                  // Roher Baseline-Stand, nicht die fertige Map: die Liste
                  // friert ihre Marker waehrend eines Drags selbst ein.
                  baselineRanks={partnerLongRanks}
                />
              )}
            </div>
          </>
        )}
      </div>

      <CreateCareerFooter>
        <div className='flex justify-end gap-x-2'>
          <Button.SecondaryXL
            onClick={() => handleBackNavigation({})}
            isLoading={isLoadingBack}
            text={t('create_career.back_action')}
          />
          <Button.PrimaryXL
            text={t('create_career.next_action')}
            isLoading={isLoadingNext}
            disabled={mode === MODE_SHORT && !hasShortKeys(shortState)}
            onClick={next}
            className='bg-green-600 hover:bg-green-500'
          />
        </div>
      </CreateCareerFooter>
    </div>
  )
}

CulturalFit.propTypes = {
  career: PropTypes.object.isRequired,
  careerMutation: PropTypes.object.isRequired
}

export default CulturalFit
